import { __awaiter } from "tslib";
/**
 * slide 接入中台后和 doc/sheet 一致使用 docx-service
 */
import { DocxChunkLoadEvent, SaveType, DocxSaveEvent, EditCommonEvent, ImporterErrorCode, MobileErrorCode, DocxLoadEvent, MobileExportErrorCode, MobileImportErrorCode, } from '@tencent/mega-sdk-protocols';
import LocalServer from '../../common/local-server-api';
import ServiceChannel from '../../common/service-channel';
import { SUCCESS_CODE } from '../../common/constants';
import retry from '../../utils/retry';
import { sleep } from '../../utils/sleep';
import { EvilPatchCanvasDOMToBlob } from './evil-patch-canvas-dom-to-blob';
import { PasswordLoadManager } from './password-load-manager';
import { ChunkContext } from './chunk-context';
const API_PATH = {
    LOAD: '/local-edit/loadDocx',
    SAVE: '/local-edit/saveDocx',
    SAVE_SSR_DATA: '/local-edit/saveSsrData',
    EDIT: '/local-edit/editDocx',
    REPORT: '/local-edit/report',
    CHUNK_IMPORT: '/local-edit/chunkImport',
};
const RETRY_INTERVAL = 200; // 分片请求，数据未完成 重试时间间隔
export class MegaDocxService {
    constructor(transport) {
        /**
         * ChangeLog:
         *   * `v1` 移动端本地编辑支持 Slide 分片导入，分片接口复用了 load
         *   * `v2` 同时支持桌面端和移动端分片导入，并兼容 Sheet 分片协议。
         *          分片导入接口独立为 loadByChunks，load 不在支持分片导入，去除与分片相关的事件。
         */
        this.version = 2;
        // apis 类接口因是 broker 上直接取值，如果包含内部调用需 arrow function 防止作用域错误
        this.apis = ['report', 'saveSsrData'];
        this.channels = ['load', 'loadByChunks', 'save', 'edit'];
        /**
         * 请求分片数据，自动重试
         */
        this.fetchChunkData = retry((options) => __awaiter(this, void 0, void 0, function* () {
            const chunkData = yield EvilPatchCanvasDOMToBlob.addConflictTask(() => this.localServer.get(API_PATH.CHUNK_IMPORT, options));
            // const chunkData = await this.localServer.get(API_PATH.CHUNK_IMPORT, options);
            if (chunkData.code === ImporterErrorCode.kSlideChunkPageEmptyError) {
                yield sleep(RETRY_INTERVAL);
                options.isStart = 0;
                return Promise.reject(chunkData.message);
            }
            return chunkData;
        }), {
            maxRetries: Infinity,
        });
        this.saveSsrData = (options, saveType = SaveType.Save) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.localServer.post(API_PATH.SAVE_SSR_DATA, Object.assign(Object.assign({}, options), { saveType }));
            }
            catch (error) {
                // eslint-disable-next-line no-console
                console.error(`[mega] MegaDocxService error: ${JSON.stringify(error)}`);
            }
        });
        /**
         * @deprecated
         * 应使用 mega-report-service 中上报方式
         */
        this.report = (options, action) => __awaiter(this, void 0, void 0, function* () {
            void this.localServer.post(API_PATH.REPORT, Object.assign(Object.assign({}, options), { action }));
        });
        this.fetchBackups = (filePath) => {
            void filePath;
            throw new Error('Method not implemented.');
        };
        this.fetchVersionMap = (filePath) => {
            void filePath;
            throw new Error('Method not implemented.');
        };
        this.removeVersion = (filePath, versionType) => {
            void [filePath, versionType];
            throw new Error('Method not implemented.');
        };
        this.localServer = new LocalServer();
        this.transport = transport;
    }
    /**
     * 跟pc端接口保持一致 返回 Promise (所有接口一致)
     * @param options
     * @returns
     */
    load(options) {
        const loadChannel = new ServiceChannel(this.transport);
        void this.startLoad(loadChannel, options);
        return Promise.resolve(loadChannel);
    }
    loadByChunks(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (options.responseType === 'arraybuffer') {
                throw new Error('responseType arraybuffer is not supported in mobile');
            }
            const channel = new ServiceChannel(this.transport);
            this.loadByChunksIntoChannel(channel, options).catch((err) => {
                channel.publish(DocxChunkLoadEvent.Error, this.createMobileError(err));
            });
            return channel;
        });
    }
    save(options, saveType) {
        const saveChannel = new ServiceChannel(this.transport);
        void this.startSave(saveChannel, options, saveType);
        return Promise.resolve(saveChannel);
    }
    edit(options) {
        void options;
        const editChannel = new ServiceChannel(this.transport);
        // 文档发生变更
        editChannel.subscribe(EditCommonEvent.ContentModified, (isModified, title) => {
            var _a, _b;
            (_b = (_a = window.mqq).invoke) === null || _b === void 0 ? void 0 : _b.call(_a, 'docx', 'localEditStatus', {
                isModified,
                title,
            });
        });
        return Promise.resolve(editChannel);
    }
    /**
     *
     * @param loadChannel
     * @param options
     * @param hasSubscribePassword 是否已注册需要密码监听
     * @returns
     */
    startLoad(loadChannel, options, hasSubscribePassword) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { code, data, message } = yield this.localServer.get(API_PATH.LOAD, options);
                if (code === SUCCESS_CODE) {
                    loadChannel.publish(DocxLoadEvent.Data, data);
                    return;
                }
                // 监听输入密码回调
                if (!hasSubscribePassword &&
                    (code === MobileImportErrorCode.kPasswordRequired || code === MobileImportErrorCode.kInvalidPassword)) {
                    loadChannel.subscribe(DocxLoadEvent.ContinueWithPassword, (password) => {
                        void this.startLoad(loadChannel, Object.assign(Object.assign({}, options), { password }), true);
                    });
                }
                loadChannel.publish(DocxLoadEvent.MobileError, code, message);
            }
            catch (err) {
                loadChannel.publish(DocxLoadEvent.MobileError, MobileImportErrorCode.ErrorUnknown, String(err));
            }
        });
    }
    loadByChunksIntoChannel(channel, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const chunkContext = new ChunkContext();
            const unpatch = EvilPatchCanvasDOMToBlob.patch();
            const dispose = () => {
                unpatch();
                channel.close();
            };
            PasswordLoadManager.load((setPassword) => {
                channel.subscribe(DocxChunkLoadEvent.ContinueWithPassword, setPassword);
                return (password) => __awaiter(this, void 0, void 0, function* () {
                    // 分片推送次数
                    let totalChunkCount = 0;
                    while (chunkContext.next()) {
                        const { code, data, total, message } = yield this.fetchChunkData(Object.assign(Object.assign(Object.assign({}, options), chunkContext.getChunkOptions()), { isStart: chunkContext.isInitialized() ? 0 : 1, password }));
                        if (code !== SUCCESS_CODE) {
                            channel.publish(DocxChunkLoadEvent.Error, this.createConverterError(message, code));
                            if (!PasswordLoadManager.isPasswordError(code)) {
                                dispose();
                            }
                            return code;
                        }
                        if (!chunkContext.isInitialized()) {
                            if (total !== undefined) {
                                totalChunkCount = total;
                            }
                            chunkContext.init(totalChunkCount);
                            channel.publish(DocxChunkLoadEvent.Head, data, totalChunkCount);
                        }
                        else {
                            channel.publish(DocxChunkLoadEvent.Chunk, data, chunkContext.chunkIndex, totalChunkCount);
                        }
                    }
                    channel.publish(DocxChunkLoadEvent.End, totalChunkCount);
                    dispose();
                    return SUCCESS_CODE;
                });
            }).catch((err) => {
                channel.publish(DocxChunkLoadEvent.Error, this.createMobileError(err));
                dispose();
            });
        });
    }
    startSave(saveChannel, options, saveType) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { convertDir, newPath, newTitle, newGlobalPadId, code, message } = yield this.localServer.post(API_PATH.SAVE, Object.assign(Object.assign({}, options), { saveType }));
                if (code === SUCCESS_CODE) {
                    // TODO: 是否需要区分保存 json/document
                    saveChannel.publish(DocxSaveEvent.JSONSaved, convertDir);
                    saveChannel.publish(DocxSaveEvent.DocumentSaved, newPath, newTitle, newGlobalPadId);
                }
                else {
                    saveChannel.publish(DocxSaveEvent.MobileError, code, message);
                }
            }
            catch (err) {
                saveChannel.publish(DocxSaveEvent.MobileError, MobileExportErrorCode.ErrorUnknown, String(err));
            }
        });
    }
    createMobileError(err, code = MobileErrorCode.ErrorUnknown) {
        return {
            name: err.name,
            code,
            message: err.message,
            stack: err.stack,
        };
    }
    createConverterError(message, code) {
        return {
            name: 'ConverterError',
            code,
            message,
        };
    }
}
export default MegaDocxService;
//# sourceMappingURL=mega-docx-service.js.map